import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Spinner } from '../ui/components';
import { Icon } from '../ui/icons';
import { useT } from '../i18n';
import { publicInvitesApi } from '../features/users/usersApi';
import './LoginPage.css';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = useT();
  const token = params.get('token') || '';

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicInvitesApi.preview(token);
        if (!cancelled) setPreview(res);
      } catch {
        if (!cancelled) setPreview({ valid: false, reason: 'This invitation link is invalid.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) { setError(t('Please enter your name.')); return; }
    if (password.length < 8) { setError(t('Password must be at least 8 characters.')); return; }
    if (password !== confirm) { setError(t('Passwords do not match.')); return; }
    setSubmitting(true);
    try {
      await publicInvitesApi.accept({ token, firstName: firstName.trim(), lastName: lastName.trim(), password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err?.message || t('Could not accept the invitation.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ps-scope pp-login">
      <aside className="pp-login__brand">
        <div className="pp-login__logo">
          <span className="pp-login__logo-mark">P</span>
          <span>Prostay Procurement</span>
        </div>
        <div className="pp-login__pitch">
          <h1 className="pp-login__headline">{t("You've been invited.")}</h1>
          <p className="pp-login__sub">{t("Set your password to join your team's procurement workspace.")}</p>
        </div>
      </aside>

      <section className="pp-login__panel">
        <div className="pp-login__card">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={28} /></div>
          ) : done ? (
            <>
              <h2 className="pp-login__title">{t("You're all set")}</h2>
              <p className="pp-login__desc">{t('Your account is ready. Redirecting you to sign in…')}</p>
            </>
          ) : preview?.valid ? (
            <>
              <h2 className="pp-login__title">{t('Join {name}', { name: preview.organizationName })}</h2>
              <p className="pp-login__desc">{t('Accepting invitation for')} <strong>{preview.email}</strong>.</p>
              <form className="pp-login__form" onSubmit={submit}>
                {error && (
                  <div className="pp-login__error" role="alert">
                    <Icon.CircleAlert size={16} />
                    <span>{error}</span>
                  </div>
                )}
                <Input label={t('First name')} autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <Input label={t('Last name')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                <Input label={t('Password')} type="password" autoComplete="new-password" placeholder={t('At least 8 characters')} leadingIcon={<Icon.Lock size={16} />} value={password} onChange={(e) => setPassword(e.target.value)} />
                <Input label={t('Confirm password')} type="password" autoComplete="new-password" leadingIcon={<Icon.Lock size={16} />} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>{t('Create account')}</Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="pp-login__title">{t('Invitation unavailable')}</h2>
              <p className="pp-login__desc">{t(preview?.reason || 'This invitation link is invalid or has expired.')}</p>
              <Button variant="secondary" size="lg" fullWidth onClick={() => navigate('/login')}>{t('Go to sign in')}</Button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
